import mutationService from '@/services/mutationService'

/**
 * Module Vuex pour la gestion des mutations et de la synchronisation
 */
const state = {
  mutations: [],
  syncStats: {
    total: 0,
    pending: 0,
    synced: 0,
    propertiesWithToSync: 0,
    syncInProgress: false
  },
  loading: false,
  error: null,
  lastSyncResult: null,
  syncListenerUnsubscribe: null
}

const mutations = {
  SET_MUTATIONS(state, mutations) {
    state.mutations = mutations
  },
  
  SET_SYNC_STATS(state, stats) {
    state.syncStats = stats
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  SET_ERROR(state, error) {
    state.error = error
  },
  
  SET_LAST_SYNC_RESULT(state, result) {
    state.lastSyncResult = result
  },
  
  SET_SYNC_LISTENER_UNSUBSCRIBE(state, unsubscribe) {
    state.syncListenerUnsubscribe = unsubscribe
  },
  
  UPDATE_SYNC_IN_PROGRESS(state, inProgress) {
    state.syncStats.syncInProgress = inProgress
  }
}

const actions = {
  /**
   * Initialise l'écoute des événements de synchronisation
   */
  initializeSyncListener({ commit, dispatch }) {
    const unsubscribe = mutationService.addSyncListener((event) => {
      
      switch (event.type) {
        case 'sync_started':
          commit('UPDATE_SYNC_IN_PROGRESS', true)
          break
          
        case 'sync_completed':
          commit('UPDATE_SYNC_IN_PROGRESS', false)
          commit('SET_LAST_SYNC_RESULT', {
            success: true,
            syncedCount: event.syncedCount,
            totalCount: event.totalCount,
            errors: event.errors || [],
            timestamp: new Date().toISOString()
          })
          // Rafraîchir les statistiques
          dispatch('loadSyncStats')
          break
          
        case 'sync_failed':
          commit('UPDATE_SYNC_IN_PROGRESS', false)
          commit('SET_LAST_SYNC_RESULT', {
            success: false,
            reason: event.reason,
            timestamp: new Date().toISOString()
          })
          break
          
        case 'mutation_stored':
          // Rafraîchir les statistiques quand une mutation est stockée
          dispatch('loadSyncStats')
          break
      }
    })
    
    commit('SET_SYNC_LISTENER_UNSUBSCRIBE', unsubscribe)
  },
  
  /**
   * Nettoie l'écoute des événements
   */
  cleanupSyncListener({ state, commit }) {
    if (state.syncListenerUnsubscribe) {
      state.syncListenerUnsubscribe()
      commit('SET_SYNC_LISTENER_UNSUBSCRIBE', null)
    }
  },
  
  /**
   * Charge les mutations en attente de synchronisation
   */
  async loadPendingMutations({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const mutations = await mutationService.getPendingMutations()
      commit('SET_MUTATIONS', mutations)
      
      commit('SET_LOADING', false)
      return mutations
    } catch (error) {
      console.error('Erreur lors du chargement des mutations:', error)
      commit('SET_ERROR', error.message)
      commit('SET_LOADING', false)
      return []
    }
  },
  
  /**
   * Charge les statistiques de synchronisation
   */
  async loadSyncStats({ commit }) {
    try {
      const stats = await mutationService.getSyncStats()
      commit('SET_SYNC_STATS', stats)
      return stats
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error)
      return null
    }
  },
  
  /**
   * Force la synchronisation de toutes les mutations en attente
   */
  async forceSynchronization({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      commit('UPDATE_SYNC_IN_PROGRESS', true)
      
      const result = await mutationService.forceSynchronization()
      
      commit('SET_LAST_SYNC_RESULT', {
        ...result,
        timestamp: new Date().toISOString()
      })
      
      // Rafraîchir les données
      await dispatch('loadPendingMutations')
      await dispatch('loadSyncStats')
      
      commit('SET_LOADING', false)
      commit('UPDATE_SYNC_IN_PROGRESS', false)
      
      return result
    } catch (error) {
      console.error('Erreur lors de la synchronisation forcée:', error)
      commit('SET_ERROR', error.message)
      commit('SET_LOADING', false)
      commit('UPDATE_SYNC_IN_PROGRESS', false)
      
      return {
        success: false,
        syncedCount: 0,
        totalCount: 0,
        errors: [error.message]
      }
    }
  },
  
  /**
   * Nettoie les mutations synchronisées anciennes
   */
  async cleanupSyncedMutations({ dispatch }, olderThanDays = 7) {
    try {
      const deleted = await mutationService.cleanupSyncedMutations(olderThanDays)
      
      // Rafraîchir les données
      await dispatch('loadPendingMutations')
      await dispatch('loadSyncStats')
      
      return deleted
    } catch (error) {
      console.error('Erreur lors du nettoyage des mutations:', error)
      return 0
    }
  },
  
  /**
   * Réinitialise l'état des erreurs et résultats
   */
  resetState({ commit }) {
    commit('SET_ERROR', null)
    commit('SET_LAST_SYNC_RESULT', null)
  }
}

const getters = {
  /**
   * Récupère toutes les mutations en attente
   */
  pendingMutations: state => state.mutations,
  
  /**
   * Récupère les statistiques de synchronisation
   */
  syncStats: state => state.syncStats,
  
  /**
   * Indique si une synchronisation est en cours
   */
  isSyncInProgress: state => state.syncStats.syncInProgress,
  
  /**
   * Nombre de mutations en attente
   */
  pendingCount: state => state.syncStats.pending,
  
  /**
   * Indique si des mutations sont en attente
   */
  hasPendingMutations: state => state.syncStats.pending > 0,
  
  /**
   * Récupère le statut de chargement
   */
  isLoading: state => state.loading,
  
  /**
   * Récupère l'erreur actuelle
   */
  error: state => state.error,
  
  /**
   * Récupère le dernier résultat de synchronisation
   */
  lastSyncResult: state => state.lastSyncResult,
  
  /**
   * Indique si la dernière synchronisation a réussi
   */
  lastSyncSuccess: state => {
    return state.lastSyncResult ? state.lastSyncResult.success : null
  },
  
  /**
   * Récupère le nombre de propriétés avec flag toSync
   */
  propertiesNeedingSync: state => state.syncStats.propertiesWithToSync
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
